import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  DesignV2Scope,
  EmptyState,
  ListRow,
  Menu,
} from "../design-v2";
import { FilterRail, StackHeader } from "../components/product";
import { buildOrigin, resolveBackTarget } from "../features/weeklyPlans/navigation";
import {
  getCycleDetailsPath,
  getCyclesLibraryPath,
} from "../features/multiWeek/routes";
import { getAllCycles } from "../services/api";
import {
  ChevronRightIcon,
  CycleMetadata,
  LIBRARY_STATUS_FILTERS,
  LibraryLoading,
  LibrarySearchField,
  PlusIcon,
  SlidersIcon,
  SparkleIcon,
} from "./programLibraryShared";
import "./programLibrary.css";

function formatDateRange(startDate, endDate) {
  if (!startDate || !endDate) {
    return "Dates unavailable";
  }

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    return `${formatter.format(new Date(`${startDate}T00:00:00`))} - ${formatter.format(
      new Date(`${endDate}T00:00:00`)
    )}`;
  } catch {
    return `${startDate} - ${endDate}`;
  }
}

export default function AllCycles() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [cycles, setCycles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadCycles() {
      setIsLoading(true);

      try {
        const response = await getAllCycles();

        if (isMounted) {
          setCycles(response.items || []);
        }
      } catch {
        if (isMounted) {
          setCycles([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadCycles();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredCycles = useMemo(() => {
    const matchingCycles = cycles.filter((cycle) => {
      const matchesFilter =
        activeFilter === "all" ? true : cycle.editorialStatus === activeFilter;

      const matchesSearch = cycle.name
        .toLowerCase()
        .includes(searchQuery.trim().toLowerCase());

      return matchesFilter && matchesSearch;
    });

    const temporalSortOrder = {
      active: 0,
      upcoming: 1,
      past: 2,
    };

    return [...matchingCycles].sort((left, right) => {
      const leftOrder = temporalSortOrder[left.temporalStatus] ?? 99;
      const rightOrder = temporalSortOrder[right.temporalStatus] ?? 99;

      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      if (left.temporalStatus === "past" && right.temporalStatus === "past") {
        return new Date(right.endDate) - new Date(left.endDate);
      }

      return new Date(left.startDate) - new Date(right.startDate);
    });
  }, [activeFilter, cycles, searchQuery]);

  function navigateToManualNew() {
    navigate("/program/manual-new", {
      state: {
        from: buildOrigin(location),
        returnTo: getCyclesLibraryPath(),
      },
    });
  }

  return (
    <DesignV2Scope className="lz-library" data-testid="all-cycles-root">
      <StackHeader
        title="All Cycles"
        backLabel="Back"
        onBack={() => navigate(resolveBackTarget(location, "/program"))}
        trailing={
          <Menu
            open={isCreateMenuOpen}
            onOpenChange={setIsCreateMenuOpen}
            trigger={
              <Button
                size="icon"
                className="lz-library__create-trigger"
                aria-label="Create program"
              >
                <PlusIcon />
              </Button>
            }
            menuClassName="lz-library__create-menu"
            aria-label="Create program options"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() =>
                navigate("/ai", {
                  state: {
                    from: buildOrigin(location),
                  },
                })
              }
            >
              <SparkleIcon />
              AI builder
            </button>
            <button type="button" role="menuitem" onClick={navigateToManualNew}>
              <SlidersIcon />
              Manual
            </button>
          </Menu>
        }
      />

      <main className="lz-library__content">
        <LibrarySearchField
          value={searchQuery}
          onChange={setSearchQuery}
          label="Search your library"
        />

        <FilterRail
          label="Cycle status filters"
          options={LIBRARY_STATUS_FILTERS}
          value={activeFilter}
          onChange={setActiveFilter}
        />

        {isLoading ? (
          <LibraryLoading label="Loading your cycles" />
        ) : filteredCycles.length === 0 ? (
          <EmptyState
            className="lz-library__empty"
            title="No cycles yet"
            body="Convert a weekly template into a multi-week cycle to populate this library."
            action={
              <Button className="lz-library__empty-action" onClick={navigateToManualNew}>
                Create your first program
              </Button>
            }
          />
        ) : (
          <Card padding="none" className="lz-library__list" data-testid="all-cycles-list">
            {filteredCycles.map((cycle) => (
              <div className="lz-library__row" data-testid="all-cycles-row" key={cycle.id}>
                <ListRow
                  className="lz-library__row-main"
                  title={cycle.name}
                  description={`${cycle.sessionsPerWeek} workouts / week${
                    typeof cycle.totalWeeklySets === "number"
                      ? ` • ${cycle.totalWeeklySets} total sets`
                      : ""
                  }`}
                  meta={
                    <>
                      <span className="lz-library__cycle-span">
                        {cycle.durationWeeks} weeks &bull; {formatDateRange(cycle.startDate, cycle.endDate)}
                      </span>
                      <CycleMetadata
                        editorialStatus={cycle.editorialStatus}
                        temporalStatus={cycle.temporalStatus}
                      />
                    </>
                  }
                  trailing={<ChevronRightIcon />}
                  onClick={() =>
                    navigate(getCycleDetailsPath(cycle.id), {
                      state: {
                        from: buildOrigin(location),
                        returnTo: getCyclesLibraryPath(),
                      },
                    })
                  }
                />
              </div>
            ))}
          </Card>
        )}
      </main>
    </DesignV2Scope>
  );
}
